import type { EpgInfo, TvChannel, TvChannelWithEpg } from '@setplex/tria-api'
import type { TFunction } from '~/core/i18n'
import type { Range, RangePosition } from '~/entities/epg/programs'
import type { AdjustPurchaseableFn } from '~/features/payment/sse-purchases'
import type {
  LiveProgramCardInfo,
  LiveProgramCardLocalizedInfo,
  TvChannelWithEpgAndLocalizedPrice,
} from '../index.h'

import { produce } from 'immer'
import { Day, NO_EPG_ID, rangePosition, State } from '~/entities/epg/programs'
import {
  DATE_FORMATS,
  formatDateDuration,
  formatDateTimeUnified,
  formatStartTime,
  formatTimeRangeUnified,
  timeWithin,
} from '~/shared/helpers/date'
import { getLabelPrice } from '~/shared/tools'

const formatEpgTime = ({
  t,
  actualStartMs,
  actualStopMs,
  leftMs,
  position,
}: Readonly<{
  t: TFunction
  actualStartMs: number
  actualStopMs: number
  leftMs: number
  position: RangePosition
}>) => {
  // for live program format description as left time
  if (position.state === State.Ongoing) {
    const { hours, minutes } = timeWithin(leftMs)
    const formattedDuration = formatDateDuration({
      duration: { hours, minutes },
      short: true,
    })

    const timeLeft = t('time_left', {
      time: formattedDuration,
    })
    return timeLeft
  }

  // for past programs format description as duration
  if (position.state === State.Elapsed) {
    return formatTimeRangeUnified(actualStartMs, actualStopMs)
  }

  // for future today programs format description as "today" + start time
  if (position.starts === Day.Today) {
    const today = t('today')
    const startTime = formatStartTime(
      actualStartMs,
      DATE_FORMATS.HH_MM_LOCALIZED
    )
    return `${today}, ${startTime}`
  }

  // for future tomorrow programs format description as "tomorrow" + start time
  if (position.starts === Day.Tomorrow) {
    const tomorrow = t('tomorrow')
    const startTime = formatStartTime(
      actualStartMs,
      DATE_FORMATS.HH_MM_LOCALIZED
    )
    return `${tomorrow}, ${startTime}`
  }

  // for far future programs format description as date + start time
  return formatDateTimeUnified(actualStartMs)
}

/**
 * Format program to show in EPG grid, with localization
 */
export const formatProgramToLocalizedView =
  ({ t, blockedByAcl }: { t: TFunction; blockedByAcl: boolean }) =>
  (program: Readonly<LiveProgramCardInfo>): LiveProgramCardLocalizedInfo => {
    // format locked title
    const lockedTitle = t('locked_content_with_name', {
      name: program.channelTitle,
    })

    // format description
    const description =
      program.position.state === State.Ongoing
        ? program.channelTitle
        : formatStartTime(program.actualStartMs, DATE_FORMATS.HH_MM_LOCALIZED)

    // fallback title depends on channel ACL block status
    const fallbackTitle = blockedByAcl ? t('geoblock_title') : t('no_epg_data')

    return {
      ...program,
      fallbackTitle,
      lockedTitle,
      description,
      descriptionHover: formatEpgTime({ t, ...program }),
    }
  }

/**
 * Format program to show in EPG grid
 */
export const formatProgramToView =
  ({ now }: { now: number }) =>
  (program: Readonly<EpgInfo>): LiveProgramCardInfo => {
    const position = rangePosition(
      [program.actualStartMs, program.actualStopMs],
      now
    )
    return {
      programId: program.id,
      programTitle: program.name,
      channelTitle: String(program.title),
      startMs: program.startMs,
      stopMs: program.stopMs,
      actualStartMs: program.actualStartMs,
      actualStopMs: program.actualStopMs,
      leftMs: program.actualStopMs - now,
      isPast: position.state === State.Elapsed,
      isLive: position.state === State.Ongoing && Boolean(program.name),
      position,
    }
  }

/**
 * Format channel with epg data to channel with live epg data
 * This means that strings are updated each minute to show how much time left and so on
 * ! `t` should be passed to `getLabelPrice` function, but now is not used
 */
export const formatToLocalizedView =
  ({ adjust, t: _ }: { adjust: AdjustPurchaseableFn; t: TFunction }) =>
  (channel: Readonly<TvChannelWithEpg>): TvChannelWithEpgAndLocalizedPrice => {
    channel = adjust(channel)
    return {
      ...channel,
      labelPrice: getLabelPrice(channel),
    }
  }

/**
 * Clip channel's programs to range, so that only visible parts of programs are shown
 */
export const clipToRange =
  ([from, to]: Range) =>
  (channel: Readonly<TvChannelWithEpg>): TvChannelWithEpg => {
    const clipped: EpgInfo[] = []
    for (const program of channel.program) {
      // program is outside of range
      if (program.actualStopMs <= from || program.actualStartMs >= to) {
        continue
      }

      // visible part of program
      const visible = produce(program, (draft) => {
        let startMs = program.actualStartMs
        let stopMs = program.actualStopMs

        // program is partially visible, clip it from the start
        if (program.actualStartMs < from && program.actualStopMs > from) {
          startMs = from
        }

        // program is partially visible, clip it from the end
        if (program.actualStartMs < to && program.actualStopMs > to) {
          stopMs = to
        }

        draft.startMs = startMs
        draft.stopMs = stopMs
      })

      clipped.push(visible)
    }

    return produce(channel, (draft) => {
      draft.program = clipped
    })
  }

/**
 * Extends channel with epg data
 */
export const extendWithProgram =
  (programs: ReadonlyMap<string, readonly Readonly<EpgInfo>[]>) =>
  (channel: Readonly<TvChannel>): TvChannelWithEpg => {
    const program = programs.get(channel.epgId ?? NO_EPG_ID) || []
    return {
      ...channel,
      program: program.map(
        produce((draft) => {
          // I'm not sure, what property `title` should be used for, but this property is not used
          // anywhere in EPG grid, so I'm using it to put channel number + channel name here,
          // this allows me to use it later in `formatProgramToLocalizedView` function,
          // so that I don't need to pass whole channel to it, simplifying `evolve` usage
          draft.title = `${channel.channelNumber}. ${channel.name}`
        })
      ),
    }
  }
